import {Dialog} from '@headlessui/react'
import classNames from 'classnames'
import {ScrollTrigger} from 'gsap/ScrollTrigger'
import React from 'react'

import Logo from '@/assets/Logo.tsx'
import IconClose from '@/assets/specify/IconClose'
import Buttons from '@/components/Buttons'
import Link from '@/components/Link'

import LogoLink from './LogoLink'
import NavBackButton from './NavBackButton'
import NavLink from './NavLink'
import NavLinkFirstLevel from './NavLinkFirstLevel'
import RessourceCardLink from './RessourceCardLink'

export default function NavMobileMenu({
  logoLink,
  items,
  buttons,
  mobileMenuOpen,
  setMobileMenuOpen,
}) {
  const [activeDepth, setActiveDepth] = React.useState(1)
  const [activeNav, setActiveNav] = React.useState(null)
  const [activeSubNav, setActiveSubNav] = React.useState(null)

  const handleSubMenuClick = (e, navLabel) => {
    e.preventDefault()
    e.stopPropagation()
    setActiveDepth((prev) => prev + 1)
    setActiveNav(navLabel)
  }

  const handleBackClick = (label) => {
    if (activeDepth >= 2) {
      setActiveNav(label)
      setActiveDepth((prev) => prev - 1)
    } else {
      setActiveNav(null)
      setActiveDepth(1)
    }
  }

  const handleClose = () => {
    setMobileMenuOpen(false)
    ScrollTrigger.normalizeScroll(true)
    setActiveDepth(1)
  }

  return (
    <Dialog as='div' className='lg:hidden' open={mobileMenuOpen} onClose={setMobileMenuOpen}>
      <div className='fixed inset-0 z-10' />
      <Dialog.Panel className='fixed inset-y-0 z-10 flex w-full flex-col overflow-y-auto bg-white'>
        <div className='flex items-center justify-between border-b border-purple-200 bg-white p-4'>
          <Link link={logoLink}>
            <Logo aria-hidden />
          </Link>
          <button
            type='button'
            className='h-10 w-10 rounded-full bg-purple-900 p-2'
            onClick={handleClose}
          >
            <span className='sr-only'>Close menu</span>
            <span aria-hidden='true' className='text-white'>
              <IconClose />
            </span>
          </button>
        </div>
        <div className='relative flex h-full w-full flex-col justify-between overflow-hidden p-4 md:px-0 md:pb-0'>
          <div className='flex w-full flex-col items-start gap-6 md:mx-auto md:max-w-[500px]'>
            {items.map((item, index) => {
              const label = item.label
              const subItems = item.items
              const link = item.link
              return (
                <>
                  {link && link.cached_url ? (
                    <div
                      className={classNames(
                        'heading-xs-satoshi flex w-full items-center justify-start  border-b border-purple-200 py-1'
                      )}
                    >
                      <Link
                        link={link}
                        className='flex w-full items-center justify-between text-purple-900'
                      >
                        <NavLinkFirstLevel label={label} />
                      </Link>
                    </div>
                  ) : (
                    <div
                      className={classNames(
                        'heading-xs-satoshi flex w-full items-center justify-start  border-b border-purple-200 py-1'
                      )}
                      onClick={(e) => handleSubMenuClick(e, label)}
                    >
                      <NavLinkFirstLevel label={label} />
                    </div>
                  )}
                  <div
                    className={classNames(
                      'absolute left-[100vw] top-0 z-10 flex h-full w-full flex-col gap-8 bg-white px-4 pt-4 transition-all duration-300 ease-out md:h-[80%] md:overflow-visible md:px-12',
                      {
                        'z-20 -translate-x-[100vw] overflow-y-auto':
                          activeDepth === 2 && activeNav === label,
                        'translate-x-0': activeDepth === 1,
                        'z-20 -translate-x-[200vw]': activeDepth >= 3 && activeNav === label,
                      }
                    )}
                  >
                    <NavBackButton
                      label={label}
                      handleBackClick={handleBackClick}
                      activeLabel={label}
                    />
                    <div
                      className={classNames('flex flex-col', {
                        'gap-8 md:grid md:grid-cols-2': index !== 0,
                      })}
                    >
                      {subItems &&
                        subItems.map((subItem) => {
                          const links = subItem.links
                          const subItemComponent = subItem.component
                          const items = subItem.items
                          return (
                            <div className={classNames('w-full')}>
                              <div
                                key={subItem._uid}
                                onClick={(e) => {
                                  handleSubMenuClick(e, label)
                                  setActiveSubNav(subItem.heading)
                                }}
                              >
                                {subItemComponent === 'navigation-item-column' && (
                                  <>
                                    <div className='body-m-medium flex w-full items-center justify-between border-b border-purple-200 py-7 pt-4 text-purple-900'>
                                      {subItem.heading}
                                      <svg
                                        width='16'
                                        height='16'
                                        viewBox='0 0 16 16'
                                        fill='none'
                                        xmlns='http://www.w3.org/2000/svg'
                                      >
                                        <g id='16 * 16'>
                                          <path
                                            id='Vector'
                                            d='M8.63393 13.2797L14.034 7.92955L8.77612 2.72026M1.96606 7.92722L13.8361 7.92722'
                                            stroke='#280C2D'
                                            strokeLinecap='round'
                                          />
                                        </g>
                                      </svg>
                                    </div>
                                    <div
                                      className={classNames(
                                        'absolute left-[100%] top-0 h-full w-full flex-col gap-7 overflow-auto bg-white px-4 pt-4 md:px-12',
                                        {
                                          hidden: activeSubNav !== subItem.heading,
                                          flex: activeSubNav === subItem.heading,
                                        }
                                      )}
                                    >
                                      <NavBackButton
                                        label={subItem.heading}
                                        handleBackClick={handleBackClick}
                                        activeLabel={label}
                                      />
                                      <div className='gap-x-6 md:grid md:grid-cols-2'>
                                        {items.map((item) => {
                                          return <NavLink {...item} />
                                        })}
                                      </div>
                                    </div>
                                  </>
                                )}
                                {subItemComponent === 'navigation-links-title-v2' && (
                                  <div className='heading-xs-millik'>{subItem.heading}</div>
                                )}
                                {links &&
                                  links.map((link) => {
                                    const style = link.style
                                    if (style === 'icon') {
                                      return <NavLink {...link} />
                                    } else if (style === 'ressource') {
                                      return <RessourceCardLink {...link} />
                                    } else if (style === 'logo') {
                                      return <LogoLink {...link} />
                                    } else {
                                      return <NavLink {...link} />
                                    }
                                  })}
                              </div>
                            </div>
                          )
                        })}
                    </div>
                  </div>
                </>
              )
            })}
          </div>
          <div className='md:relative md:z-[99] md:border-t md:border-purple-200 md:bg-white md:pb-4'>
            <div className='md:mx-auto md:max-w-[500px]'>
              <Buttons
                buttons={buttons}
                buttonProps={{hasIcon: false, size: 'small'}}
                className='mt-20 flex flex-col items-center gap-4 md:flex-row [&>a]:w-full'
              />
            </div>
          </div>
        </div>
      </Dialog.Panel>
    </Dialog>
  )
}
